import { groupBy } from '@bettermode/common/utils'
import { ColorScheme, ThemeColors } from '@tribeplatform/design-system/types'
import { useI18n } from '@tribeplatform/react-components/i18n'
import { ColorPicker } from '@tribeplatform/react-ui-kit/ColorPicker'
import { Text } from '@tribeplatform/react-ui-kit/Text'

import { SidebarBackButton } from '../../AdminMenu/SidebarBackButton.js'
import { SidebarContent } from '../../AdminMenu/SidebarContent.js'
import { useCustomizer, useCustomizerTheme } from '../hooks/index.js'
import { CustomizerActionType } from '../types.js'
import { getTranslatedColorScheme } from './utils.js'

const SECTION_ORDER = {
  background: 1,
  surface: 2,
  content: 3,
  action: 4,
  default: 100,
}

interface AppearanceEditThemeProps {
  colorScheme: ColorScheme
  onClose: () => void
}

export const AppearanceEditTheme = ({
  colorScheme,
  onClose,
}: AppearanceEditThemeProps) => {
  const { dispatch } = useCustomizer()
  const { $t } = useI18n()

  const {
    appearance: { theme: currentTheme },
  } = useCustomizerTheme()

  const colorTokens: ThemeColors = currentTheme[colorScheme]

  const onTokenChange = (key: string, value: string) => {
    // if there is no custom theme, we create one. if custom theme exists, send only diff
    const updatedTheme = {
      ...currentTheme,
      id: 'custom',
      name: 'Custom',
      [colorScheme]: {
        ...currentTheme[colorScheme],
        [key]: value,
      },
    }

    dispatch({
      type: CustomizerActionType.NEW_UPDATE,
      payload: {
        theme: {
          typography: {
            customTheme: updatedTheme,
          },
        },
      },
    })
  }

  const editableTokens = Object.keys(colorTokens).filter(
    it =>
      !it.match(
        /decorative|positive|negative|destructive|attention|highlight|overlay|backdrop|disabled/,
      ),
  )

  const tokensBySection = groupBy(editableTokens, key => key.split('-')[0])

  const sections = Object.keys(tokensBySection).sort(
    (a, b) =>
      (SECTION_ORDER[a] ?? SECTION_ORDER.default) -
      (SECTION_ORDER[b] ?? SECTION_ORDER.default),
  )

  return (
    <SidebarContent
      header={<SidebarBackButton onClick={onClose} />}
      title={$t({
        defaultMessage: 'Edit theme style',
        id: 'Admin.Sidebar.EditThemeStyle',
      })}
      description={$t({
        defaultMessage:
          'Changes you make here affect all sections that use this theme.',
        id: 'Admin.Sidebar.EditThemeStyleDescription',
      })}
    >
      <div className="space-y-4">
        <Text variant="headingXs">
          {getTranslatedColorScheme($t, colorScheme)}
        </Text>
        <div className="flex flex-col gap-6">
          {sections.map(section => (
            <div key={section} className="flex flex-col gap-2">
              <Text variant="bodyXl" className="capitalize">
                {section}
              </Text>
              {tokensBySection[section].sort().map(tokenName => (
                <div
                  key={tokenName}
                  className="flex items-center gap-2 justify-between"
                >
                  <div>{tokenName}</div>
                  <ColorPicker
                    color={colorTokens[tokenName]}
                    onChange={value => onTokenChange(tokenName, value)}
                    placement="right"
                    arrow
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </SidebarContent>
  )
}
